import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  signal,
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Guid, isNil, Nillable } from '@web/utils';
import { MultimediaChooseOrUploadComponent } from '../multimedia-choose-or-upload/multimedia-choose-or-upload.component';
import { MultimediaType } from '../../../shared/enums/multimedia-type';
import { AdminMultimediaImageUpload } from '../../models/admin-multimedia-image-upload';

@Component({
  selector: 'app-multimedia-choose-or-upload-control',
  imports: [MultimediaChooseOrUploadComponent],
  templateUrl: './multimedia-choose-or-upload-control.component.html',
  styleUrl: './multimedia-choose-or-upload-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultimediaChooseOrUploadControlComponent),
      multi: true,
    },
  ],
})
export class MultimediaChooseOrUploadControlComponent implements ControlValueAccessor {
  protected readonly disabled = signal(false);
  protected readonly multimediaId = signal<Nillable<Guid>>(null);

  /* prettier-ignore */
  protected readonly allowedMimeTypes = 'image/png, image/apng, image/vnd.mozilla.apng, image/jpeg, image/gif, image/webp, image/svg+xml, image/svg';
  protected readonly maxFileSize = 5 * 1000 * 1000;
  protected readonly allowedMultimediaTypes: MultimediaType[] = [
    MultimediaType.Image,
  ];

  private onChange!: (value: Nillable<AdminMultimediaImageUpload>) => void;
  private onTouched!: () => void;

  public writeValue(value: Nillable<AdminMultimediaImageUpload>): void {
    if (isNil(value)) {
      this.multimediaId.set(null);

      return;
    }

    const { id } = value;

    this.multimediaId.set(id);
  }

  public registerOnChange(
    fn: (value: Nillable<AdminMultimediaImageUpload>) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public handleChooseClickEvent(id: Guid): void {
    this.onChange(new AdminMultimediaImageUpload({ id }));
    this.onTouched();
  }

  public handleClearClickEvent(): void {
    this.onChange(null);
    this.onTouched();
  }
}
